<template>
	<Modal v-model="show">
		<h2>Terms and conditions: <em>ok</em>HiQ submission form</h2>
		<div class="terms">
			<!-- Submission form terms -->
			<ol>
				<li>
					<p><strong>Submissions</strong></p>
					<p>
						Submitting content does not guarantee display on HiQ or QUT digital
						signage. Content must be appropriate and relevant to the topic at
						the time of submission. Submissions are reviewed on a case-by-case
						basis at the sole discretion of HiQ.
					</p>
				</li>
				<li>
					<p><strong>Copyright</strong></p>
					<ol>
						<li>
							Copyright in content submitted must be owned by the submitter,
							jointly owned by the submitter, or must be licensed in such a way
							that use of that content on HiQ and QUT digital signage does not
							infringe copyright.
						</li>
						<li>
							If copyright is jointly owned you must have the consent of the
							other copyright owners to submit the content. QUT will not
							intervene in disputes between joint content owners, and excludes
							all liability arising out of or in connection with jointly owned
							copyright content. Your content will be used by QUT in accordance
							with
							<a
								target="_blank"
								href="https://www.mopp.qut.edu.au/F/F_05_01.jsp"
								>MOPP F/5.1 Copyright</a
							>.
						</li>
					</ol>
				</li>
				<li>
					<p><strong>Terms</strong></p>
					<ol>
						<li>
							QUT regularly reproduces content supplied in its publications,
							promotional and marketing material and on its website. This
							content is used to promote the University or describe its
							activities. It may be used for educational, publicity and
							advertising purposes in any media and for commercial or
							non-commercial purposes of the University.
						</li>
						<li>
							Content submitted to HiQ for display could include, but is not
							limited to: images including photographs, drawings, writing,
							visual art and more.
						</li>
						<li>
							Your completion of the submission form will constitute formal
							acceptance for QUT to use your content, which may be capable of
							personally identifying you, for the stated purposes. It is your
							responsibility to ensure that you obtain all appropriate consents
							from any person who can be identified in your content prior to
							submitting.
						</li>
						<li>
							QUT has obligations to ensure your information and content is used
							and disclosed only in ways which are consistent with Queensland
							privacy principles and applicable laws (see
							<a
								target="_blank"
								href="https://www.mopp.qut.edu.au/F/F_06_02.jsp"
								>MOPP F/6.2 Information Privacy</a
							>). In general, content is not published unless the University has
							obtained the consent of the individual concerned.
						</li>
					</ol>
				</li>
				<li>
					<p><strong>Competitions</strong></p>
					<ol>
						<li>
							Submission Competitions run by HiQ with <em>ok</em>HiQ will be
							governed by these terms and conditions as well as the
							<em>ok</em>HiQ Promotional and Competition Terms and Conditions.
						</li>
					</ol>
				</li>
				<li>
					<p><strong>Acknowledgement</strong></p>
					<p>
						QUT wishes to display, publish, use, store and reproduce your
						content for the purposes outlined in this Form, and is seeking your
						consent to do so.
					</p>
				</li>
			</ol>
			<p>
				<strong
					>By submitting this electronic Form, I acknowledge and agree
					that:</strong
				>
			</p>
			<ol>
				<li>
					I am at least eighteen (18) years of age and have the requisite
					capacity to provide my consent on this Form. If you are under the age
					of 18 years, you will be deemed to have obtained the permission of
					your parent or legal guardian.
				</li>
				<li>
					I have read, understand and have raised any concerns about the
					information contained in this Form with QUT staff before accepting
					these terms and conditions and providing my consent;
				</li>
				<li>
					The University may display, use, reproduce (including the right to
					alter, amend, modify or take any other action) and disclose my content
					(subject to any moral rights that I may have under the
					<em>Copyright Act 1968</em> (Cth) as explained above;
				</li>
				<li>
					I will not receive any payment, and will not make any claim against
					QUT for any payment or other fee , in respect of my content appearing
					on QUT’s digital screens, in QUT promotional material or
					advertisements, and release QUT from all other claims arising out of
					the University’s use of my content;
				</li>
				<li>
					<p>
						I have obtained all required permissions, approvals and/or consents
						to provide the content to QUT, including:
					</p>
					<ol type="a">
						<li>
							consent from all other copyright owners in respect of jointly
							owned content; and
						</li>
						<li>
							Privacy and other consent from all individuals appearing in the
							content.
						</li>
					</ol>
				</li>
				<li>
					I warrant that the submitted content, and its use by QUT, will not
					infringe the rights (including intellectual property rights) of any
					third party, and indemnify QUT against any loss, claim or damage
					arising out of or in connection with breach of this warranty.
				</li>
				<li>
					<p>Once my content is submitted to QUT:</p>
					<ol type="a">
						<li>
							I will grant to QUT a non-exclusive enduring, irrevocable,
							perpetual license to use my content as described in the ‘Terms’
							section of this Form;
						</li>
						<li>
							I understand that I waive any right that I may have to review or
							approve the content prior to the content being used or reproduced
							by QUT; and
						</li>
						<li>
							I understand that my permission and consent for QUT to use my
							content may be revoked at any time by providing written notice to
							QUT at
							<a href="mailto:hiq.communications@qut.edu.au"
								>hiq.communications@qut.edu.au</a
							>.
						</li>
					</ol>
				</li>
			</ol>
			<!-- General terms -->
			<h2 class="mt">OKHIQ PROMOTIONAL AND COMPETITION TERMS AND CONDITIONS</h2>
			<p>
				From time to time HiQ will run competitions with <em>ok</em>HiQ which
				will be commenced through a Competition announcement. These terms and
				conditions apply to all <em>ok</em>HiQ Submission competitions unless
				stated otherwise.
			</p>
			<h3>PART A - PROMOTION DETAILS</h3>
			<p>
				This QUT Competition is conducted in accordance with the
				<strong>Part A Competition Details</strong> and the
				<strong>Part B Terms and Conditions</strong>.
			</p>
			<table>
				<tbody>
					<tr class="header">
						<th colspan="2">PARTICULARS</th>
					</tr>
					<tr>
						<th>Competition Name</th>
						<td>As outlined in the Competition announcement.</td>
					</tr>
					<tr>
						<th>ITEM 1<br />Competition enquiries</th>
						<td>
							All Competition enquiries can be directed to
							<a href="mailto:hiq.communications@qut.edu.au"
								>hiq.communications@qut.edu.au</a
							>.
						</td>
					</tr>
					<tr>
						<th>ITEM 2<br />Competition Description</th>
						<td>As described in the relevant Competition announcement.</td>
					</tr>
					<tr>
						<th>ITEM 3<br />Competition Period</th>
						<td>
							<p>
								Competition Start Date: As outlined in the Competition
								announcement.
							</p>
							<p>
								Competition Closing Date: As outlined in the Competition
								announcement.
							</p>
							<p>
								<strong
									>Entries will not be accepted after the Closing Date.</strong
								>
							</p>
						</td>
					</tr>
					<tr>
						<th>ITEM 4<br />Eligibility Requirements</th>
						<td>
							<p>
								To be eligible to win a prize in the Competition, participants
								must:
							</p>
							<ul>
								<li>Provide their name and email address.</li>
							</ul>
							<p>
								The Promoter reserves the right, at any time and in its absolute
								discretion, to disqualify any Participant that does not meet the
								eligibility requirements.
							</p>
						</td>
					</tr>
					<tr>
						<th>ITEM 5<br />How to enter</th>
						<td>
							<p>
								To Enter this Competition, a Participant may submit
								<strong>multiple</strong> Entries. If multiple Entries are
								permitted, each Entry must be submitted separately.
							</p>
							<p>
								Competition Entries must be submitted by using okhiq.qut.edu.au
								between the
								<strong
									>Competition Start Date and Midnight AEST on the Competition
									End Date</strong
								>.
							</p>
							<p>
								The Promoter reserves the right, at any time and in its absolute
								discretion, to (a) verify the validity of any Entry; and (b)
								disqualify any Invalid Entry, or any Entry submitted not in
								accordance with these Terms.
							</p>
						</td>
					</tr>
					<tr>
						<th>ITEM 6<br />Prize(s)</th>
						<td>As outlined in the Competition announcement.</td>
					</tr>
					<tr>
						<th>ITEM 7<br />Selection of prize winner(s)</th>
						<td>
							<p>
								All valid Entries will be displayed for voting. The winner will
								be the Submission with the most votes. In the event of a tie,
								the winner will be chosen at the promoter’s discretion.
							</p>
							<p>Participants accept the following:</p>
							<ul>
								<li>
									Entries are displayed randomly to voters and there is no
									guarantee that your entry will be displayed;
								</li>
								<li>
									If your entry is displayed it may not be in any consistent
									manner.
								</li>
								<li>
									Entries that comply with the selection criteria may be judged
									more favourably by the voters and may have an increased chance
									of winning; and
								</li>
								<li>
									There is no guarantee that the voters will judge fairly
									against the voting criteria.
								</li>
							</ul>
							<p>The above factors make the competition a game of chance.</p>
							<p><strong>PROMOTION SELECTION CRITERIA:</strong></p>
							<p>
								The prize winner(s) will be selected by the Promoter
								<strong
									>on the selection date outlined in the Competition
									announcement</strong
								>.
							</p>
							<p><strong>ANNOUNCEMENT OF PRIZE WINNER(S):</strong></p>
							<p>
								The Promoter will use reasonable endeavours to (a) announce a
								prize winner by QUT channels; and (b) notify the prize winner by
								no later than
								<strong>seven days after the Competition End date</strong>.
							</p>
						</td>
					</tr>
					<tr>
						<th>ITEM 8<br />Claiming the prize</th>
						<td>
							<p>
								The prize winner(s) must claim their prize within 7 days of
								being notified that they are the prize winner (the “<strong
									>Forfeiture Date</strong
								>”) by:
							</p>
							<ol type="a">
								<li>
									collecting the prize in person at HiQ on QUT campus, between
									9:00am – 5:00pm AEST, at Level 3 V Block, 2 George Street,
									Brisbane; or
								</li>
								<li>
									Where agreed, QUT at its own expense will make arrangements
									for shipping of the prize to a prize winner.
								</li>
							</ol>
							<p>
								If a prize winner does not claim their prize before the
								specified Forfeiture Date, the winner shall forfeit their prize
								and the Promoter will select a replacement prize winner by
								random draw in accordance with these Terms.
							</p>
						</td>
					</tr>
					<tr>
						<th>ITEM 9<br />Participant’s Personal Information</th>
						<td>
							<p>
								In order to participate in this Competition, Participants may be
								required to provide the Promoter with personal information such
								as the Participant’s:
							</p>
							<ul>
								<li>Name</li>
								<li>Email address</li>
							</ul>
						</td>
					</tr>
				</tbody>
			</table>
			<h3>PART B - TERMS AND CONDITIONS FOR COMPETITION</h3>
			<ol>
				<li>
					Part A details the promotional Terms and Conditions. These Part B
					Terms and Conditions and all information on “how to enter” this
					Competition (collectively the “<strong>Terms</strong>”) apply to this
					promotion (the “<strong>Competition</strong>”).
				</li>
				<li>
					A <strong>Competition announcement</strong> means information on the
					QUT <em>ok</em>HiQ website that advertises an <em>ok</em>HiQ
					competition.
				</li>
				<li>
					The Competition will be conducted by the Queensland University of
					Technology (ABN: 83 791 724 622, CRICOS Provider No. 00213J) of 2
					George Street, Brisbane, QLD, 4000 (“<strong>QUT</strong>” or the
					“<strong>Promoter</strong>”) during the Competition Period specified
					in Part A.
				</li>
				<li>
					By entering this Competition, you (the “<strong>Participant</strong>”)
					warrant that you have read, understood and agree to be bound by the
					Terms.
				</li>
				<li>
					If the Participant is under the age of 18 years, the Participant will
					be deemed to have obtained the permission of the Participant’s parent
					or legal guardian to participate in the Competition and for the
					Promoter to use the Competition Entry as set out in these Terms.
				</li>
				<li>
					The Promoter reserves the right to amend or replace these Terms and/or
					any Competition rules or procedures at any time and for any reason.
				</li>
				<li>
					Interpretation. Any reference to “includes” or “including” means
					without limitation, and where permitted in Part A, any reference to
					(a) Participant includes multiple Participants entering the
					Competition as a team; (b) Competition Entry includes multiple
					Entries; and (c) Prize includes multiple prizes.
				</li>
			</ol>
			<p><strong>COMPETITION ENTRY REQUIREMENTS</strong></p>
			<ol class="continue">
				<li>
					All entries become the property of the Promoter upon submission,
					upload or otherwise.
				</li>
				<li>
					No responsibility is accepted for late, lost, incorrectly submitted or
					misdirected entries.
				</li>
				<li>
					Competition Entries must be received by the Promoter during the
					Competition Period.
				</li>
				<li>
					Entries that are incomplete, indecipherable, illegible, forged,
					manipulated, tampered with in any way, contain any defamatory,
					offensive or unlawful content, any Entry that has the potential to
					damage the reputation of any individual or entity, or entries that the
					Promoter reasonably considers do not comply with these Terms (“<strong
						>Invalid Entries</strong
					>”). All Invalid Entries will be removed from the Competition.
				</li>
				<li>
					<p>
						If a Participant is required to submit a literary, dramatic, musical
						or artistic work (a “<strong>Work</strong>”) as their Competition
						Entry (whether in written, audio, electronic or visual form, or any
						combination thereof), the Participant warrants that:
					</p>
					<ol type="a">
						<li>
							their Entry has not previously been, and will not be, published or
							submitted in connection with any other competition until after the
							Competition Period ends.
						</li>
						<li>
							Their Entry is entirely original work by the Participant; and
						</li>
						<li>
							<p>
								They have obtained all required permissions, approvals and/or
								consents in respect of the Entry, including:
							</p>
							<ol type="i">
								<li>
									all necessary copyright title or interest in their Entry to
									entitle the Participant to submit the Entry and to effectively
									provide the Promoter with the rights in relation to the Entry
									set out in these Terms;
								</li>
								<li>
									privacy and other consents from all individuals appearing in
									the work; and
								</li>
								<li>consents from any property owners (as applicable).</li>
							</ol>
						</li>
						<li>
							their Entry is not, and its use by the Promoter, will not infringe
							the rights (including intellectual property rights) of any third
							party, and indemnifies the Promoter against any loss, claim or
							damage arising out of or in connection with breach of this
							warranty.
						</li>
					</ol>
				</li>
				<li>
					Participants grant to the Promoter, at no cost to the Promoter, a
					royalty free, irrevocable, perpetual, worldwide, non-exclusive licence
					to use (including reproduce), the Participant’s Entry for: (a)
					promotional and educational purposes relating to the Competition; and
					(b) any other promotional purposes relating to QUT.
				</li>
			</ol>
			<p><strong>JUDGING AND PRIZES</strong></p>
			<ol class="continue">
				<li>
					<p>
						This promotion is a game of chance. Each validly submitted Entry may
						have an increased chance of winning if the public votes for it
						because their Entry complies with any outlined selection criteria
						however:
					</p>
					<ol type="a">
						<li>
							The Entries are displayed using a computer algorithm which is
							random and based on chance. The Promoter cannot guarantee that any
							Entry will be displayed to voters;
						</li>
						<li>
							There is no guarantee that members of the public will vote based
							on skill or merit; and
						</li>
						<li>
							If your Entry is displayed to voters it will appear at random
							times and there is no guarantee of consistency as to when it will
							be displayed to voters.
						</li>
					</ol>
				</li>
				<li>
					A Participant claiming to be a prize winner must provide proof of
					identity, if required by the Promoter. Each prize winner may also be
					required to sign a document indicating they have received their prize
					at the time of collection.
				</li>
				<li>
					If a prize winner is under the age of 18 years, their prize may be
					awarded to the prize winner’s parent or legal guardian.
				</li>
				<li>
					The Promoter may, but is not obliged, to publish the results of the
					Competition. By entering this Competition, the participant agrees to
					the Promoter publishing their name as a winner in any media whatsoever
					(unless otherwise advised by the Participant during the Competition
					Period).
				</li>
				<li>
					Prizes are subject to availability. If a prize is unavailable, the
					Promoter, in its absolute discretion, reserves the right to substitute
					the prize with a prize to the same or similar value and/or
					specification.
				</li>
				<li>
					If a prize includes vouchers, tickets or is of a similar redeemable
					nature, the prize is valid until the expiry date specified on the
					prize (or as otherwise notified by the prize provider), and is subject
					at all times to any terms and conditions specified by the prize
					provider.
				</li>
				<li>
					The Promoter is not responsible for any changes in dates, times,
					cancellations or otherwise that may prevent a prize winner from
					accepting, taking or using a prize.
				</li>
				<li>
					The Promoter makes no warranties or representations, and will not
					accept any liability, in respect of the fitness for purpose or
					suitability of any prize, or the failure of any prize to be of
					merchantable quality. If liability is imposed by legislation and
					cannot be excluded the Promoter, the liability of the Promoter in
					respect of the relevant goods and/or services is limited to (a)
					re-supply; or (b) paying replacement costs.
				</li>
				<li>
					The prize(s) or any part or element of the prize(s) are not
					transferable, exchangeable or redeemable for cash.
				</li>
				<li>
					The manufacturer(s) or supplier(s) of the prize(s) are not
					participants in, or promoters or sponsors of, this Competition.
				</li>
			</ol>
			<p><strong>GENERAL</strong></p>
			<ol class="continue">
				<li>This Competition is free to enter.</li>
				<li>
					Any decision of the Promoter in respect of all matters arising out of
					or in connection with the Competition is final and binding and no
					correspondence will be entered into.
				</li>
				<li>
					Each Participant is responsible for notifying the Promoter of any
					change to the Participant’s contact details during and after the
					Competition Period.
				</li>
				<li>
					If this Competition is interfered with in any way or is not capable of
					being conducted as anticipated due to any reason beyond reasonable
					control of the Promoter, the Promoter reserves the right, in its sole
					discretion to modify, suspend, terminate or cancel the Competition, as
					appropriate.
				</li>
				<li>
					<strong>Jurisdiction</strong>. This Competition and these Terms are
					governed by the laws of Queensland.
				</li>
			</ol>
			<p><strong>DISCLAIMER</strong></p>
			<ol class="continue">
				<li>Participants enter the Competition at their own risk.</li>
				<li>
					Except for any liability that cannot be excluded by law, the Promoter
					(including its officers, employees and agents) excludes all liability
					(including liability in contract, tort, pursuant to legislation or
					otherwise), and is hereby released, from any personal injury or any
					loss or damage (including loss of opportunity), whether direct,
					indirect, special or consequential, arising in any way out of the
					Competition or any prize, including but not limited to, where arising
					out of the following: (a) any technical difficulties or equipment
					malfunction (whether or not under the Promoter's control); (b) any
					theft, unauthorised access or third party interference; (c) any entry
					or prize claim that is late, lost, altered, damaged or misdirected
					(whether or not after their receipt by the Promoter) due to any reason
					beyond the reasonable control of the Promoter; (d) any tax liability
					incurred by a prize winner; (e) any expense incurred by a prize winner
					in connection with the prize or Competition generally, including spare
					parts, maintenance/repair, delivery costs or otherwise associated with
					accepting, taking or using a prize are the prize; (f) Possession, use
					or misuse of a prize; or (g) participation in any activity associated
					with the Competition.
				</li>
			</ol>
			<p><strong>PRIVACY STATEMENT</strong></p>
			<ol class="continue">
				<li>
					QUT is bound by the Information Privacy Act 2009 (Qld) (“IPA”), and
					the QUT privacy policy is available at
					<a target="_blank" href="https://www.qut.edu.au/additional/privacy"
						>https://www.qut.edu.au/additional/privacy</a
					>
					and
					<a target="_blank" href="https://www.mopp.qut.edu.au/F/F_06_02.jsp"
						>https://www.mopp.qut.edu.au/F/F_06_02.jsp</a
					>
				</li>
				<li>
					The Promoter will collect and use the Participants' personal
					information for the purposes of administering and carrying out the
					Competition, and may be used to send information and future
					promotional communications to the Participant.
				</li>
				<li>
					If a Participant does not consent to the collection, use and
					disclosure of personal information as outlined in these Terms and
					Conditions, the Participant should contact the Promoter.
				</li>
				<li>
					A request to access, update or correct any information should be
					directed to the Promoter.
				</li>
			</ol>
		</div>
	</Modal>
</template>

<script>
import Modal from '@/components/main/Modal'

import modal from '@/mixins/modal'
const modalTerms = modal('terms')

export default {
	name: 'ModalTerms',
	mixins: [modalTerms],
	components: { Modal }
}
</script>

<style lang="scss" scoped>
@import '~scss/layout';

.terms {
	font-size: 14px;

	.mt {
		margin-top: 3rem;
	}
}

ol {
	list-style-type: none;
	margin: 0;
	padding: 0;

	&:not(.continue) {
		counter-reset: item;
	}

	> li {
		display: table;
		counter-increment: item;
		margin-bottom: 0.6em;

		:last-child {
			margin-bottom: 0;
		}

		&::before {
			content: counters(item, '.') '. ';
			display: table-cell;
			padding-right: 0.6em;
		}
	}

	&[type='a'] > li::before {
		content: counter(item, lower-alpha) '. ';
	}

	&[type='i'] > li::before {
		content: counter(item, lower-roman) '. ';
	}
}

li ol:not([type='a']):not([type='i']) > li::before {
	content: counters(item, '.') '. ';
}

ul {
	> li {
		display: table;
		margin-bottom: 0.6em;

		> :first-child {
			margin-top: 0;
			color: red;
		}
		> :last-child {
			color: red;
			margin-bottom: 0;
		}

		&::before {
			content: '•';
			display: table-cell;
			padding-right: 0.6em;
		}
	}
}

table {
	width: 100%;
	border-collapse: collapse;

	tr {
		&:not(.header) th {
			text-align: left;
			vertical-align: top;
		}
	}

	th,
	td {
		@include truncate-margin;
		border: 1px solid white;
		padding: 0.5em;
	}
}
</style>
